import React from 'react';
import { Minus, Plus } from 'lucide-react';

export interface TemperatureSettingStyle01Props {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: number) => void;
  label?: string;
  unit?: string;
  showLabel?: boolean;
}

/**
 * TemperatureSetting Style 01 - Stepper
 * Large temperature display with +/- buttons
 * Command: ThermostatTemperatureSetpoint
 */
export const Style01_Stepper: React.FC<TemperatureSettingStyle01Props> = ({
  value,
  min = 16,
  max = 32,
  step = 1,
  disabled = false,
  loading = false,
  onChange,
  label = '溫度設定',
  unit = '°C',
  showLabel = true
}) => {
  const handleDecrease = () => {
    const newValue = Math.max(min, value - step);
    onChange(newValue);
  };

  const handleIncrease = () => {
    const newValue = Math.min(max, value + step);
    onChange(newValue);
  };

  const isAtMin = value <= min;
  const isAtMax = value >= max;

  return (
    <div className="space-y-3">
      {showLabel && (
        <div className="text-center">
          <span className="text-sm text-slate-400">{label}</span>
        </div>
      )}
      <div className="flex items-center justify-center gap-4">
        {/* Decrease Button */}
        <button
          onClick={handleDecrease}
          disabled={disabled || loading || isAtMin}
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
            isAtMin || disabled
              ? 'bg-slate-800/30 text-slate-600 cursor-not-allowed'
              : 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 active:scale-95'
          }`}
        >
          <Minus className="w-6 h-6" />
        </button>

        {/* Temperature Display */}
        <div className="flex items-baseline justify-center min-w-[140px]">
          <span className={`text-7xl font-light tracking-tight ${
            disabled ? 'text-slate-500' : 'text-white'
          }`}>
            {loading ? '--' : value}
          </span>
          <span className={`text-3xl font-light ml-1 ${
            disabled ? 'text-slate-600' : 'text-slate-400'
          }`}>
            {unit}
          </span>
        </div>

        {/* Increase Button */}
        <button
          onClick={handleIncrease}
          disabled={disabled || loading || isAtMax}
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
            isAtMax || disabled
              ? 'bg-slate-800/30 text-slate-600 cursor-not-allowed'
              : 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 active:scale-95'
          }`}
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default Style01_Stepper;
